import { Component, Input } from '@angular/core';

import { MyBetsConfig, SitecoreMyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { ThemeOptions } from '@frontend/sports/common/ui/shared';
import { PopupAction } from '@frontend/sports/modal/feature';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { DsButtonKind, DsButtonVariant } from '@frontend/ui/button';
import { Store } from '@ngrx/store';
import { ExternalBetslipActions } from 'packages/sports/common/betslip/core/external-betslip-actions';
import { EditMyBetPicksPayload } from 'packages/sports/common/betslip/model/edit-mybet/edit-bet-init';
import { Observable } from 'rxjs';

import { ArcRestrictionService } from '../../../arc/arc-restriction.service';
import { EpcotConfigService } from '../../../common/epcot-config.service';
import { GeolocationCheckService } from '../../../geo-location/geo-location-check.service';
import { MyBetsBetSlip, MyBetsListContext } from '../../../my-bets-base/models/my-bets-viewmodels';
import { PopupManager } from '../../../popup/popup-manager.service';
import { EditMyBetButtonStateService } from '../../services/edit-my-bet-button-state.service';
import { MyBetsPicksFactoryService } from '../../services/my-bets-picks-factory.service';

@Component({
    selector: 'ms-my-bets-edit-bet-button',
    templateUrl: './my-bets-edit-bet-button.component.html',
    styleUrl: './my-bets-edit-bet-button.scss',
    standalone: false,
})
export class MyBetsEditBetButtonComponent {
    @Input({ required: true }) betslip: MyBetsBetSlip;
    @Input({ required: true }) myBetsListContext: MyBetsListContext;

    isUserOnPlayBreak$: Observable<boolean>;
    isEpcotEnabled: boolean;

    enableRedesignedBetCard = this.myBetsConfig.enableRedesignedBetCard;

    readonly buttonThemeKind: ThemeOptions<DsButtonKind> = {
        'mgm-4': 'utility',
        'sports-interaction': 'utility',
        'betboo-br': 'utility',
        'sportingbet-br': 'utility',
    };

    constructor(
        private myBetsPickFactory: MyBetsPicksFactoryService,
        private trackingService: TrackingService,
        public sitecoreMyBets: SitecoreMyBetsConfig,
        private geolocationCheckService: GeolocationCheckService,
        private editMyBetButtonStateService: EditMyBetButtonStateService,
        private store: Store,
        private arcRestrictionService: ArcRestrictionService,
        private popupManager: PopupManager,
        private epcotConfigService: EpcotConfigService,
        private myBetsConfig: MyBetsConfig,
    ) {
        this.isUserOnPlayBreak$ = this.arcRestrictionService.isUserOnPlayBreak$;
        this.isEpcotEnabled = this.epcotConfigService.isEnabled();
    }

    async click($event: Event): Promise<void> {
        $event.stopPropagation();
        $event.preventDefault();

        if (!this.isEnabled) {
            return;
        }

        this.trackingService.track(trackingConstants.EVENT_EDIT_BET, {
            [trackingConstants.PAGE_REFERRING_ACTION]: 'Sports_MyBets_EditBet_Clicked',
            [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
        });

        this.geolocationCheckService.checkGeolocation(
            (isLocationFailed) => {
                if (!isLocationFailed) {
                    this.editBet();
                }
            },
            { waitForLocationAvailable: true },
        );
    }

    async editBet(): Promise<void> {
        const payload = await this.myBetsPickFactory.createPayload(this.betslip, this.myBetsListContext);
        if (payload) {
            this.startEditMyBet(payload);
        }
    }

    private startEditMyBet(editMyBetPicksPayload: EditMyBetPicksPayload): void {
        if (this.epcotConfigService.isEnabled()) {
            this.popupManager.dismissPreviousPopup('betslip', PopupAction.CloseByMybets);
        }
        this.store.dispatch(ExternalBetslipActions.startEditMyBet({ payload: editMyBetPicksPayload }));
    }

    get isEnabled(): boolean {
        return this.editMyBetButtonStateService.isEnabled(this.betslip);
    }

    get buttonKind(): DsButtonKind {
        return this.enableRedesignedBetCard ? 'utility' : 'tertiary';
    }

    get buttonVariant(): DsButtonVariant {
        return this.enableRedesignedBetCard ? 'flat-reduced' : 'outline';
    }

    get buttonThemeVariant(): ThemeOptions<DsButtonVariant> {
        return {
            'mgm-4': this.themeVariantForMgmBetCard(),
            'sports-interaction': 'flat',
            'betboo-br': 'flat',
            'sportingbet-br': 'flat',
        };
    }

    private themeVariantForMgmBetCard() {
        return this.enableRedesignedBetCard ? 'flat-reduced' : 'flat';
    }
}
